import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { syncImportAPI } from '../services/api';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  Upload, ArrowRight, ArrowLeft, Download, RefreshCw,
  CheckCircle, XCircle, AlertTriangle, Loader, FileSpreadsheet, Minus,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

// Must match backend routes/syncImport.js TEMPLATE / CATV_TEMPLATE — column names +
// aliases for flexible header matching (case/spacing/punctuation-insensitive).
const EXPIRY_FIELD = { key: 'expiry', header: 'expiry (DD/MM/YYYY)', aliases: ['expiry', 'expirydate', 'expdate', 'validity', 'validtill', 'expiryon'] };
const SERVICES = {
  broadband: {
    title: 'Sync Broadband Expiry',
    subtitle: 'Update expiry dates on existing customers by PPPoE username — matched to your uploaded file, dates read as DD/MM/YYYY only',
    fields: [
      { key: 'username', header: 'PPPoE Username', aliases: ['username', 'pppoe', 'pppoeusername', 'broadbanduser', 'bbusername'] },
      EXPIRY_FIELD,
    ],
    sample: { username: 'suresh_pppoe', expiry: '19/07/2028' },
    templateName: 'broadband-expiry-sync-template.csv',
    keyLabel: 'PPPoE Username',
    keyHelp: "must exactly match the customer's existing username in this system",
    notFoundLabel: 'Username not found',
    dateNote: true,
  },
  catv: {
    title: 'Sync CATV Expiry',
    subtitle: 'Update expiry dates on existing cable customers by account number — matched to your uploaded file, dates read as DD/MM/YYYY only',
    fields: [
      { key: 'account', header: 'Account Number', aliases: ['account', 'accountnumber', 'accountno', 'acno', 'acc', 'catvaccount', 'catvaccountnumber'] },
      EXPIRY_FIELD,
      { key: 'billingType', header: 'Billing Type (monthly/days)', aliases: ['billingtype', 'durationtype', 'type', 'billing'], optional: true },
      { key: 'days', header: 'Days (only for days type)', aliases: ['days', 'durationdays', 'validitydays'], optional: true },
    ],
    sample: { account: '10001', expiry: '31/07/2026', billingType: 'monthly', days: '' },
    templateName: 'catv-expiry-sync-template.csv',
    keyLabel: 'Account Number',
    keyHelp: "must exactly match the customer's CATV account number in this system",
    notFoundLabel: 'Account not found',
    dateNote: true,
    extraHelp: 'Optional: Billing Type ("monthly" for auto-renew or "days") and Days (multiple of 30, only used when Billing Type is "days") — leave both blank to leave a customer\'s billing type untouched.',
  },
  catvHardware: {
    title: 'Sync CATV Hardware',
    subtitle: 'Update STB Serial, STB Model & Entity Code on existing cable customers by account number',
    fields: [
      { key: 'account', header: 'Account Number', aliases: ['account', 'accountnumber', 'accountno', 'acno', 'acc', 'catvaccount', 'catvaccountnumber'] },
      { key: 'stbSerial', header: 'STB Serial', aliases: ['stb', 'stbno', 'settopboxserial', 'stbnumber', 'stbserialnumber'], optional: true },
      { key: 'stbModel', header: 'STB Model', aliases: ['settopboxmodel', 'stbtype', 'boxmodel'], optional: true },
      { key: 'entityCode', header: 'Entity Code', aliases: ['entity'], optional: true },
    ],
    sample: { account: '10001', stbSerial: 'STB99887766', stbModel: 'DEN HD', entityCode: 'ENT001' },
    templateName: 'catv-hardware-sync-template.csv',
    keyLabel: 'Account Number',
    keyHelp: "must exactly match the customer's CATV account number in this system",
    notFoundLabel: 'Account not found',
    extraHelp: 'STB Serial, STB Model and Entity Code are each optional per row — leave any of them blank to leave that field unchanged on that customer (a blank cell never clears an existing value). At least one of the three must be filled.',
    supportsCurrentExport: true,
  },
};

const norm = s => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');

function resolveHeaders(headers, fields) {
  const fieldByIdx = {};
  const claimed = new Set();
  headers.forEach((h, idx) => {
    if (!h) return;
    const n = norm(h);
    const field = fields.find(f => !claimed.has(f.key) &&
      (norm(f.header) === n || f.aliases.some(a => norm(a) === n)));
    if (field) { fieldByIdx[idx] = field.key; claimed.add(field.key); }
  });
  return fieldByIdx;
}

// Excel date cells arrive as JS Date objects (cellDates: true) — render as DD/MM/YYYY
// text so the strictly-day-first backend parser sees exactly what a human would type.
const cellText = c => c instanceof Date
  ? `${String(c.getDate()).padStart(2, '0')}/${String(c.getMonth() + 1).padStart(2, '0')}/${c.getFullYear()}`
  : String(c ?? '').trim();

function parseFileInBrowser(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let wb;
        if (/\.csv$/i.test(file.name)) {
          let text = e.target.result;
          if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
          // No cellDates here: SheetJS's CSV date auto-detection guesses month-first
          // for ambiguous strings like "07/10/2026", corrupting DD/MM/YYYY input before
          // our strict day-first parser ever sees it. Keep CSV cells as plain text.
          wb = XLSX.read(text, { type: 'string', raw: true });
        } else {
          wb = XLSX.read(e.target.result, { type: 'array', cellDates: true, raw: false });
        }
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        const headers = (raw[0] || []).map(h => String(h).trim());
        const rows    = raw.slice(1).filter(r => r.some(c => String(c).trim() !== ''));
        if (headers.every(h => !h)) return reject(new Error('File appears empty — no column headers found'));
        resolve({ headers, rows });
      } catch (err) {
        reject(new Error(`Cannot parse file: ${err.message}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    if (/\.csv$/i.test(file.name)) reader.readAsText(file, 'utf-8');
    else reader.readAsArrayBuffer(file);
  });
}

function buildRecords(headers, rows, fields) {
  const fieldByIdx = resolveHeaders(headers, fields);
  const records = rows.map((row, i) => {
    const rec = { _row: i + 2 };
    headers.forEach((h, idx) => {
      const key = fieldByIdx[idx];
      if (key) rec[key] = cellText(row[idx]);
    });
    return rec;
  });
  const missing = fields.filter(f => !f.optional && !Object.values(fieldByIdx).includes(f.key));
  return { records, missing };
}

function downloadTemplate(cfg) {
  const lines = [cfg.fields.map(f => f.header), cfg.fields.map(f => cfg.sample[f.key])]
    .map(r => r.map(v => /[",\n]/.test(v) ? `"${String(v).replace(/"/g, '""')}"` : v).join(','));
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = cfg.templateName;
  a.click();
  URL.revokeObjectURL(a.href);
}

const STATUS_STYLE = {
  ready:     { bg: 'var(--cb-d1fae5)', color: 'var(--c-065f46)', label: 'Will Update' },
  unchanged: { bg: 'var(--cb-e5e7eb)', color: 'var(--c-374151)', label: 'No Change' },
  not_found: { bg: 'var(--cb-fef3c7)', color: 'var(--c-92400e)', label: 'Not Found' },
  error:     { bg: 'var(--cb-fee2e2)', color: 'var(--c-991b1b)', label: 'Error' },
};

const VALIDATE_CHUNK = 500;
const RUN_BATCH = 200;

export default function SyncImportPage({ service = 'broadband' }) {
  useDarkReady();
  const navigate = useNavigate();
  const fileRef  = useRef(null);
  const cfg = SERVICES[service];

  const [step, setStep] = useState(1); // 1=upload 2=review 3=results
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');

  const [records, setRecords] = useState([]);
  const [rowStatus, setRowStatus] = useState({});
  const [errorsOnly, setErrorsOnly] = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState(null);

  const [exportStatus, setExportStatus] = useState('active');
  const [downloadingCurrent, setDownloadingCurrent] = useState(false);

  const downloadCurrentData = async () => {
    setDownloadingCurrent(true);
    try {
      const { data } = await syncImportAPI.exportCurrent({ status: exportStatus }, service);
      const rows = data.data || [];
      if (rows.length === 0) { toast.error('No matching customers found'); return; }
      const restFields = cfg.fields.slice(1); // everything after the key/account field
      const headers = [cfg.fields[0].header, 'Name', 'Current Status', ...restFields.map(f => f.header)];
      const lines = [headers, ...rows.map(r => [r.account, r.name, r.status, ...restFields.map(f => r[f.key] ?? '')])];
      const csv = lines.map(row => row.map(v => /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : v).join(',')).join('\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${cfg.templateName.replace('-template', `-current-${exportStatus}`)}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${rows.length} customers — edit the fields you want to change, leave the rest as-is`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Download failed');
    } finally {
      setDownloadingCurrent(false);
    }
  };

  const loadFile = async (file) => {
    setLoading(true); setLoadingMsg('Parsing file…');
    try {
      const { headers, rows } = await parseFileInBrowser(file);
      const { records: recs, missing } = buildRecords(headers, rows, cfg.fields);
      if (missing.length > 0) {
        toast.error(`Could not find column(s): ${missing.map(f => f.header).join(', ')}. Use the downloaded template's exact headers.`);
        return;
      }
      if (recs.length === 0) { toast.error('No data rows found in the file'); return; }

      setLoadingMsg(`Checking ${recs.length} rows…`);
      const localStatus = {};
      for (let i = 0; i < recs.length; i += VALIDATE_CHUNK) {
        const chunk = recs.slice(i, i + VALIDATE_CHUNK);
        const { data } = await syncImportAPI.validate({ records: chunk }, service);
        data.data.rows.forEach(r => { localStatus[r.row] = { status: r.status, message: r.message }; });
        setLoadingMsg(`Checking ${Math.min(i + VALIDATE_CHUNK, recs.length)} / ${recs.length} rows…`);
      }
      setRecords(recs);
      setRowStatus(localStatus);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not parse file');
    } finally {
      setLoading(false); setLoadingMsg('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const counts = useMemo(() => {
    const c = { ready: 0, unchanged: 0, not_found: 0, error: 0 };
    records.forEach(r => { const s = rowStatus[r._row]?.status; if (c[s] !== undefined) c[s]++; });
    return c;
  }, [records, rowStatus]);

  const runnable = useMemo(
    () => records.filter(r => rowStatus[r._row]?.status === 'ready'),
    [records, rowStatus]
  );

  const runSync = async () => {
    if (runnable.length === 0) { toast.error('No rows to sync'); return; }
    setSyncing(true);
    setProgress({ done: 0, total: runnable.length });
    const combined = { updated: 0, notFound: 0, failed: 0, errors: [] };
    try {
      for (let b = 0; b < runnable.length; b += RUN_BATCH) {
        const batch = runnable.slice(b, b + RUN_BATCH);
        const { data } = await syncImportAPI.run({ records: batch }, service);
        combined.updated  += data.data.updated;
        combined.notFound += data.data.notFound;
        combined.failed   += data.data.failed;
        combined.errors.push(...(data.data.errors || []));
        setProgress({ done: Math.min(b + RUN_BATCH, runnable.length), total: runnable.length });
      }
      setResults(combined);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const reset = () => {
    setStep(1); setRecords([]); setRowStatus({}); setResults(null); setErrorsOnly(false);
  };

  const visibleRows = errorsOnly
    ? records.filter(r => ['error', 'not_found'].includes(rowStatus[r._row]?.status))
    : records;

  return (
    <div style={{ maxWidth: 880, margin: '0 auto' }}>
      <div className="flex items-center gap-3 mb-6">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/customers')}>
          <ArrowLeft size={14} /> Back
        </button>
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>{cfg.title}</h2>
          <p className="text-muted text-small" style={{ margin: 0 }}>
            {cfg.subtitle}
          </p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => downloadTemplate(cfg)} style={{ marginLeft: 'auto' }}>
          <Download size={13} /> CSV Template
        </button>
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: 28, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}>
        {[
          { n: 1, label: 'Template & Upload' },
          { n: 2, label: 'Review & Sync' },
          { n: 3, label: 'Results' },
        ].map(s => (
          <div key={s.n} style={{
            flex: 1, padding: '10px 16px', textAlign: 'center',
            background: step === s.n ? 'var(--primary)' : step > s.n ? 'var(--cb-d1fae5)' : 'var(--card-bg)',
            color: step === s.n ? 'var(--c-ffffff)' : step > s.n ? 'var(--c-065f46)' : 'var(--text-muted)',
            fontWeight: step === s.n ? 700 : 500, fontSize: '0.85rem',
            borderRight: s.n < 3 ? '1px solid var(--border)' : 'none',
          }}>
            {step > s.n && <CheckCircle size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />}
            Step {s.n}: {s.label}
          </div>
        ))}
      </div>

      {/* ── STEP 1 ── */}
      {step === 1 && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <FileSpreadsheet size={34} color="var(--primary)" />
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>1. Download the CSV template</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  <strong>{cfg.keyLabel}</strong> ({cfg.keyHelp})
                  {cfg.dateNote && <> and <strong>expiry</strong> — always written as <strong>DD/MM/YYYY</strong> (e.g. 19/07/2028 for 19 July 2028).
                  Dates are never read as MM/DD/YYYY, regardless of the value.</>}
                  {cfg.extraHelp && <> {cfg.extraHelp}</>}
                </div>
              </div>
              <button className="btn btn-primary" onClick={() => downloadTemplate(cfg)}>
                <Download size={14} /> Download Template
              </button>
            </div>
          </div>

          {cfg.supportsCurrentExport && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-body" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <FileSpreadsheet size={34} color="var(--primary)" />
                <div style={{ flex: 1, minWidth: 260 }}>
                  <div style={{ fontWeight: 700, marginBottom: 2 }}>Or: download current data to edit</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    Pre-filled with every matching customer's existing values — edit only what changed, leave the rest as-is, then upload below.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-small text-muted" htmlFor="sync-export-status">Customer status:</label>
                  <select
                    id="sync-export-status"
                    className="input"
                    style={{ width: 'auto' }}
                    value={exportStatus}
                    onChange={e => setExportStatus(e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                    <option value="all">All</option>
                  </select>
                </div>
                <button className="btn btn-secondary" disabled={downloadingCurrent} onClick={downloadCurrentData}>
                  {downloadingCurrent ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />}
                  {downloadingCurrent ? 'Preparing…' : 'Download Current Data'}
                </button>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '36px 24px' }}>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
                onChange={e => e.target.files[0] && loadFile(e.target.files[0])} />
              <div
                onClick={() => !loading && fileRef.current?.click()}
                style={{ border: '2px dashed var(--cb-cbd5e1)', borderRadius: 12, padding: '32px 24px', cursor: 'pointer' }}
                onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--primary)'; }}
                onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--cb-cbd5e1)'; }}
                onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--cb-cbd5e1)'; const f = e.dataTransfer.files[0]; if (f && !loading) loadFile(f); }}
              >
                {loading
                  ? <><Loader size={32} color="var(--primary)" style={{ margin: '0 auto 12px', display: 'block', animation: 'spin 1s linear infinite' }} /><p>{loadingMsg}</p></>
                  : <><Upload size={32} color="var(--c-9ca3af)" style={{ margin: '0 auto 12px', display: 'block' }} />
                      <p style={{ fontWeight: 600, marginBottom: 4 }}>2. Click to browse or drag & drop your filled file</p>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>CSV, XLSX or XLS — parsed locally, then checked against the database. Nothing is written until you confirm on the next screen.</p>
                    </>
                }
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP 2 ── */}
      {step === 2 && (
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
            <button className="btn btn-secondary btn-sm" onClick={reset}><ArrowLeft size={13} /> Start Over</button>
            <div><strong>{records.length}</strong> rows checked against the database — nothing updated yet.</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            {[
              { k: 'ready', icon: <CheckCircle size={20} color="var(--c-10b981)" />, border: 'var(--cb-10b981)', label: 'Will update' },
              { k: 'unchanged', icon: <Minus size={20} color="var(--c-6b7280)" />, border: 'var(--cb-9ca3af)', label: 'Already correct' },
              { k: 'not_found', icon: <AlertTriangle size={20} color="var(--c-f59e0b)" />, border: 'var(--cb-f59e0b)', label: cfg.notFoundLabel },
              { k: 'error', icon: <XCircle size={20} color="var(--c-ef4444)" />, border: 'var(--cb-ef4444)', label: 'Errors' },
            ].map(({ k, icon, border, label }) => (
              <div key={k} className="card" style={{ textAlign: 'center', borderTop: `3px solid ${counts[k] > 0 ? border : 'var(--cb-e5e7eb)'}` }}>
                <div className="card-body" style={{ padding: '12px 8px' }}>
                  {icon}
                  <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{counts[k]}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{label}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Row Details</h3>
              {(counts.error > 0 || counts.not_found > 0) && (
                <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={errorsOnly} onChange={e => setErrorsOnly(e.target.checked)} />
                  Show only problems ({counts.error + counts.not_found})
                </label>
              )}
            </div>
            <div className="card-body" style={{ padding: 0, maxHeight: 420, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg)', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '7px 12px', textAlign: 'left', width: 55 }}>Row</th>
                    {cfg.fields.map(f => (
                      <th key={f.key} style={{ padding: '7px 12px', textAlign: 'left' }}>{f.header}</th>
                    ))}
                    <th style={{ padding: '7px 12px', textAlign: 'left', width: 110 }}>Status</th>
                    <th style={{ padding: '7px 12px', textAlign: 'left' }}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.slice(0, 300).map(r => {
                    const st = rowStatus[r._row] || { status: 'error', message: 'Not validated' };
                    const style = STATUS_STYLE[st.status] || STATUS_STYLE.error;
                    return (
                      <tr key={r._row} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '5px 12px', color: 'var(--text-muted)' }}>{r._row}</td>
                        {cfg.fields.map((f, i) => (
                          <td key={f.key} style={{ padding: '5px 12px', fontWeight: i === 0 ? 600 : 400 }}>{r[f.key] || '—'}</td>
                        ))}
                        <td style={{ padding: '5px 12px' }}>
                          <span style={{ background: style.bg, color: style.color, padding: '2px 8px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {style.label}
                          </span>
                        </td>
                        <td style={{ padding: '5px 12px', color: 'var(--text-muted)' }}>{st.message}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {visibleRows.length > 300 && (
                <div style={{ padding: '8px 14px', fontSize: '0.78rem', color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>
                  Showing first 300 of {visibleRows.length} rows. All {runnable.length} rows marked "Will update" will still be synced.
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn btn-secondary" onClick={reset}>Cancel</button>
            <button className="btn btn-primary" onClick={runSync} disabled={syncing || runnable.length === 0} style={{ minWidth: 220 }}>
              {syncing
                ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Syncing… {progress.done}/{progress.total}</>
                : <><RefreshCw size={14} /> Sync {runnable.length} Customers</>
              }
            </button>
          </div>

          {syncing && progress.total > 0 && (
            <div style={{ marginTop: 12, background: 'var(--card-bg)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--border)' }}>
              <div style={{ background: 'var(--border)', borderRadius: 4, height: 6 }}>
                <div style={{
                  background: 'var(--primary)', borderRadius: 4, height: 6,
                  width: `${(progress.done / progress.total) * 100}%`, transition: 'width 0.3s ease',
                }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── STEP 3 ── */}
      {step === 3 && results && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            <div className="card" style={{ flex: 1, textAlign: 'center', borderTop: '3px solid var(--cb-10b981)' }}>
              <div className="card-body">
                <CheckCircle size={28} color="var(--c-10b981)" style={{ margin: '0 auto 8px', display: 'block' }} />
                <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--c-10b981)' }}>{results.updated}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Updated</div>
              </div>
            </div>
            {results.notFound > 0 && (
              <div className="card" style={{ flex: 1, textAlign: 'center', borderTop: '3px solid var(--cb-f59e0b)' }}>
                <div className="card-body">
                  <AlertTriangle size={28} color="var(--c-f59e0b)" style={{ margin: '0 auto 8px', display: 'block' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--c-f59e0b)' }}>{results.notFound}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Not Found</div>
                </div>
              </div>
            )}
            <div className="card" style={{ flex: 1, textAlign: 'center', borderTop: `3px solid ${results.failed > 0 ? 'var(--cb-ef4444)' : 'var(--cb-e5e7eb)'}` }}>
              <div className="card-body">
                <XCircle size={28} color={results.failed > 0 ? 'var(--c-ef4444)' : 'var(--c-9ca3af)'} style={{ margin: '0 auto 8px', display: 'block' }} />
                <div style={{ fontSize: '2rem', fontWeight: 700, color: results.failed > 0 ? 'var(--c-ef4444)' : 'var(--text-muted)' }}>{results.failed}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Failed</div>
              </div>
            </div>
          </div>

          {results.errors.length > 0 && (
            <div className="card mb-4">
              <div className="card-header" style={{ background: 'var(--cb-fef2f2)', borderBottom: '1px solid var(--cb-fecaca)' }}>
                <h3 style={{ color: 'var(--c-dc2626)', margin: 0 }}>Skipped / Errors ({results.errors.length})</h3>
              </div>
              <div className="card-body" style={{ padding: 0, maxHeight: 320, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)' }}>
                      <th style={{ padding: '6px 14px', textAlign: 'left', width: 70 }}>Row</th>
                      <th style={{ padding: '6px 14px', textAlign: 'left' }}>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.errors.map((e, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '5px 14px', color: 'var(--c-ef4444)', fontWeight: 600 }}>Row {e.row}</td>
                        <td style={{ padding: '5px 14px' }}>{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={reset}>
              <RefreshCw size={14} /> Sync Another File
            </button>
            <button className="btn btn-primary" onClick={() => navigate('/customers')}>
              View Customers →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
